/**
 * Team-Chat im Browser (Story 24.1): Antworttypen und Abrufe von `/api/v1/chat`. Noch ohne Echtzeit – neue Nachrichten
 * kommen nach dem Senden und beim Neuladen (Story 24.2 bringt den Strom).
 */
import { useQuery } from '@tanstack/react-query';
import type { ChannelKind, MemberRole, ChatFileView } from '@till-erp/chat/values';
import { apiFetch } from './api.ts';
import { useSession } from './session.tsx';

export const CHAT_API = '/api/v1/chat';

export interface ChatChannelSummary {
  id: string;
  kind: ChannelKind;
  name: string;
  topic: string | null;
  everyone: boolean;
  memberCount: number;
}

export interface ChatMyChannel extends ChatChannelSummary {
  role: MemberRole;
}

export interface ChatChannelView extends ChatChannelSummary {
  membership: 'member' | 'none';
  role: MemberRole | null;
  members: { id: string; name: string; role: MemberRole }[];
}

export interface ChatMessage {
  id: string;
  channelId: string;
  authorId: string;
  authorName: string;
  body: string;
  postedAt: string;
  version?: number;
  parentMessageId?: string;
  replyCount?: number;
  reactions?: { emoji: string; count: number; active: boolean; names: string[] }[];
  editedAt?: string | null;
  deletedAt?: string | null;
  files: ChatFileView[];
}

export interface ChatMessagePage {
  messages: ChatMessage[];
  hasMore: boolean;
  settingsVersion: number;
  /** Höchstlänge einer Nachricht (Einstellung) für den Zähler in der Eingabe. */
  messageMaxChars: number;
}

export interface PostedChatMessage {
  messageId: string;
  channelId: string;
  authorId: string;
  authorName: string;
  body: string;
  postedAt: string;
  files: ChatFileView[];
  editedAt?: string | null;
  deletedAt?: string | null;
}

export const chatKeys = {
  all: ['chat'] as const,
  channels: ['chat', 'channels'] as const,
  discover: ['chat', 'discover'] as const,
  people: ['chat', 'people'] as const,
  channel: (id: string) => ['chat', 'channel', id] as const,
  messages: (id: string) => ['chat', 'messages', id] as const,
  thread: (id: string, root: string) => ['chat', 'messages', id, 'thread', root] as const,
};

export function useMyChannels() {
  const { getToken } = useSession();
  return useQuery({
    queryKey: chatKeys.channels,
    queryFn: ({ signal }) => apiFetch<{ channels: ChatMyChannel[] }>(getToken, `${CHAT_API}/channels`, { signal }).then((r) => r.channels),
  });
}

export function useDiscoverChannels(enabled: boolean) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: chatKeys.discover,
    enabled,
    queryFn: ({ signal }) => apiFetch<{ channels: ChatChannelSummary[] }>(getToken, `${CHAT_API}/channels/discover`, { signal }).then((r) => r.channels),
  });
}

export function useChatPeople(enabled: boolean) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: chatKeys.people,
    enabled,
    staleTime: 60_000,
    queryFn: ({ signal }) => apiFetch<{ people: { id: string; name: string }[] }>(getToken, `${CHAT_API}/people`, { signal }).then((r) => r.people),
  });
}

export function useChatChannel(id: string | null) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: chatKeys.channel(id ?? ''),
    enabled: !!id,
    retry: false,
    queryFn: ({ signal }) => apiFetch<ChatChannelView>(getToken, `${CHAT_API}/channels/${encodeURIComponent(id!)}`, { signal }),
  });
}
